// libraries
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
// utils
import {
  formatXAxisDates,
  formatNumberNoRoundingOff,
  downloadCSV,
  formatProductAnalyticsReportDates,
  sortMetricData
} from '../../utils/charts'

import { ExportLink, countries as countriesMap } from '@insticator/insticator-ui'

import injectSheet from 'react-jss'

import styles from './exportdataincsv_styles'

import { resetCsvDownloadStatus } from '../../state/slices/revenue_beta'
// static
import { MONETIZATION_TYPE_BETA } from '../../pages/dashboard/monetization/consts'
import { callRelevantApi } from '../../pages/dashboard/helperfunction'

/* ========== ~~~~~~~~~~ DETAILS TABLE  ~~~~~~~~~~ ========== */
const ExportDataInCsv = injectSheet(styles)((props) => {
  const details = useSelector((state) => state.revenueBeta)// getting details of the whole revenueBeta state
  const metric = props.currentMetric /* get selected metric */
  const zAxisId = props.zAxisId
  const period = props.period
  const requiredMetricData = useSelector((state) => state.revenueBeta[metric])/* selected metric data, if card has 1 toggle that, if card has multiple toggles, selected metric toggle data */
  const downloadCsvStatus = requiredMetricData ? requiredMetricData.csvdatastatus : undefined
  const csvDownloadLinkParams = requiredMetricData ? requiredMetricData.csvDownloadLinkParams : undefined
  const selectedDimension = csvDownloadLinkParams ? csvDownloadLinkParams.dimension : undefined// getting selected dimension , this will be used to send as a parameter to api
  const [isExporting, setIsExporting] = useState(false)
  const dispatch = useDispatch()
  const { account } = useSelector((state) => state)

  // function to reset the fulfilled states back to pending, to avoid redownload and recalling of apis
  const cleanUpStates = () => {
    if (csvDownloadLinkParams && csvDownloadLinkParams.metrics) {
      csvDownloadLinkParams.metrics.forEach(card => {
        dispatch(resetCsvDownloadStatus(card))
      })
    }
  }

  const getCsvDimensionMap = (dimension) => {
    const dimensionMapExisting = props.dimensionMap.map((object) => { return { id: object.id, name: object.name } })// this dimensionMap is the one which is used for dashboard view,
    const defaultMapToUse = dimensionMapExisting.concat(Object.keys(account.sitesAndAppsUUIDs).map((key) => { return { id: key, name: account.sitesAndAppsUUIDs[key] } }))// concatenating existing dimension map with all sites
    if (dimension === 'SITE' || dimension === 'DEVICE') {
      return defaultMapToUse// concatenating existing dimension map with all sites
    } else if (dimension === 'AD_UNIT') {
      if (requiredMetricData.hasOwnProperty('csvdatastatus') && Object.keys(requiredMetricData.csvdata[period]).length > 0) {
        return dimensionMapExisting.concat(Object.keys(requiredMetricData.csvdata[period]).map((key) => { return { id: key, name: key } }))// concatenating existing map with all the ad unit ids
      }
      return dimensionMapExisting
    } else if (dimension === 'GEO') {
      return dimensionMapExisting.concat(Object.keys(countriesMap).map((key) => { return { id: key, name: countriesMap[key] } }))// concatenating existing map with the countries
    } else {
      return defaultMapToUse// concatenating existing with all the sites
    }
  }

  const formatDataAsTableRows = (columns) => {
    if (!props.formattedData) return null
    let formattedData = []
    switch (props.tableType) {
      case MONETIZATION_TYPE_BETA:
        const csvDimensionMap = getCsvDimensionMap(selectedDimension)
        const requiredData = requiredMetricData.csvdata
        const selected = sortMetricData(requiredData[period], requiredData.TOTAL)
        if (zAxisId) {
          if (Object.keys(details[zAxisId][period].TOTAL).length > 0) {
            selected[zAxisId] = details[zAxisId][period].TOTAL
          } else {
            Object.keys(selected.TOTAL).forEach(period => { selected[zAxisId] = { ...selected[zAxisId], [period]: 0 } })
          }
        }
        formattedData = Object.keys(selected).map((productType) => {
          const productData = selected[productType]
          const rows = {
            ...columns, // add all columns to each row
            product: (csvDimensionMap || []).find(prod => prod.id === productType).name
          }
          if (productType === 'TOTAL' && props.totalAsAvgLabel) rows.product = props.totalAsAvgLabel

          Object.keys(rows).forEach(date => {
            if (isNaN(rows[date]) && !props.totalAsAvgLabel) {
              if ((csvDimensionMap || []).map(p => p.name).indexOf(rows[date]) < 0 && productType !== rows[date]) rows[date] = 0
            }
          }) // Prepopulate columns that doesn't have values
          Object.keys(productData).forEach(date => {
            let dataToBeFormatted = productData[date].data
            if (productData[date].hasOwnProperty('dividend') && productData[date].hasOwnProperty('divisor') && productData[date].divisor > 0) {
              dataToBeFormatted = productData[date].dividend / productData[date].divisor
            }
            rows[date] = formatNumberNoRoundingOff(dataToBeFormatted, props.metricUnit)
          })
          return rows
        })
        break
      case 'monetization':
        formattedData = props.formattedData
        break
      case 'commenting':
        formattedData = props.formattedData
        break
      case 'trivia':
        formattedData = props.formattedData
        break
      case 'trending':
        formattedData = props.formattedData
        break
      default:
        break
    }
    return formattedData
  }

  const convertDataToCSV = data => {
    let csvData = 'Product'
    let fileName = 'data'

    const dates = Object.keys(data[0]).filter(key => key != 'product')
    if (data.length > 0 && dates.length > 0) {
      // set the csv header columns
      dates.forEach(date => {
        let colDate = props.tableType === 'monetization' || props.tableType === MONETIZATION_TYPE_BETA ? formatXAxisDates(date, period) : formatProductAnalyticsReportDates(date, period)
        colDate = colDate.indexOf(',') >= 0 ? `"${colDate}"` : colDate
        csvData += `,${colDate}`
      })
      csvData += '\n'
      // set the csv data
      data.forEach(product => {
        csvData += product.product
        csvData += ','
        dates.forEach((date, index) => {
          csvData += product[date].replaceAll(',', '')
          if (index < dates.length - 1) csvData += ','
        })
        csvData += '\n'
      })
      // set the file name
      fileName = `${props.csvFileName ? props.csvFileName : 'Exported Data'} ${period.toUpperCase()} - ${dates[0]}`
      fileName = dates.length > 1 ? `${fileName} - ${dates[dates.length - 1]}` : fileName
    }

    return {
      data: csvData,
      fileName
    }
  }

  const exportCsv = () => {
    try {
      const columns = props.columns// getting precalculated columsn from detailstable
      const formattedDataForCsvDownloadView = formatDataAsTableRows(columns)// formatting rows
      const csvData = convertDataToCSV(formattedDataForCsvDownloadView)
      downloadCSV(csvData.data, `${csvData.fileName}.csv`, 'text/csv;encoding:utf-8')
      if (props.onExport) props.onExport()
      setIsExporting(false)
    } catch (err) {
      console.log('error downloading csv')
      setIsExporting(false)
      cleanUpStates()
    }
  }

  /* ---------------------------------------------CHECKING STATUS OF CSV DOWNLOADING APIS ---------------------------------------- */
  if (downloadCsvStatus === 'fulfilled') {
    exportCsv()// check if zaxis is present, if yes download that data also
    cleanUpStates() // starting fresh
  } else if (downloadCsvStatus === 'rejected') {
    setIsExporting(false)
    cleanUpStates() // starting fresh
  }
  /* --------------------------------------------------------------------------------------------------------------------------- */

  const handleExport = () => {
    if (!isExporting) {
      try {
        setIsExporting(true)// block another request from going into this
        cleanUpStates() // starting fresh
        if (props.tableType === MONETIZATION_TYPE_BETA)// handling cases for monetization beta
        {
          if (requiredMetricData && Object.keys(requiredMetricData.csvdata).length > 0) {
            exportCsv()//checking if data is already there no need to call api
          }
          else {
            const paramsToPass = JSON.parse(JSON.stringify(csvDownloadLinkParams))
            delete paramsToPass.limit
            dispatch(callRelevantApi(paramsToPass, false))
          }
        } else// handling case for triva, polls and commenting download, simply download formatted data
        {
          exportCsv()
        }
      }
      catch (err) {
        console.log('error downloading csv')
        setIsExporting(false)
      }
    }
  }

  return (
    <ExportLink action={handleExport} exporting={isExporting} />
  )
})

export default ExportDataInCsv
